import { memo } from 'react';
import cuid from 'cuid';
import _ from 'lodash';
import { ShapeType } from '../types';

const STAR_1 = (color: string): string => {
  return `data:image/svg+xml,%3Csvg width='24' height='24' fill='%23${color.replace(
    '#',
    '',
  )}' stroke='%23${color.replace(
    '#',
    '',
  )}' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.951 2.927c-.299-.921-1.602-.921-1.902 0L9.53 7.601a1 1 0 0 1-.951.69H3.665c-.968 0-1.372 1.24-.588 1.81l3.976 2.888a1 1 0 0 1 .363 1.118l-1.518 4.674c-.3.921.755 1.688 1.538 1.118l3.976-2.888a1 1 0 0 1 1.176 0l3.976 2.888c.783.57 1.838-.196 1.538-1.118l-1.518-4.674a1 1 0 0 1 .363-1.118l3.976-2.888c.783-.57.381-1.81-.588-1.81H15.42a1 1 0 0 1-.95-.69l-1.519-4.674Z'%3E%3C/path%3E%3C/svg%3E`;
};
const STAR_2 = (color: string): string => {
  return `data:image/svg+xml;charset=utf-8,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' fill='%23${color.replace(
    '#',
    '',
  )}'%3E%3Cpath d='M12 1C12.3708 1 12.7111 1.20512 12.8843 1.53295L15.8685 7.18279L22.1703 8.27196C22.536 8.33516 22.8365 8.5956 22.9511 8.94851C23.0657 9.30142 22.9755 9.68877 22.7167 9.95471L18.26 14.5353L19.1702 20.8575C19.223 21.2246 19.0684 21.5908 18.7684 21.8088C18.4684 22.0269 18.0724 22.0611 17.7395 21.8976L12 19.0786L6.26054 21.8976C5.92766 22.0611 5.53163 22.0269 5.23166 21.8088C4.93169 21.5908 4.77704 21.2246 4.82989 20.8575L5.7401 14.5353L1.2833 9.95471C1.02455 9.68877 0.934315 9.30142 1.04891 8.94851C1.16351 8.5956 1.46409 8.33516 1.82972 8.27196L8.13159 7.18279L11.1158 1.53295C11.2889 1.20512 11.6293 1 12 1Z'/%3E%3C/svg%3E`;
};
const HEART_1 = (color: string): string => {
  return `data:image/svg+xml;charset=utf-8,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' fill='%23${color.replace(
    '#',
    '',
  )}'%3E%3Cpath d='M8.4 5.25c-2.78 0-5.15 2.08-5.15 4.78 0 1.863.872 3.431 2.028 4.73 1.153 1.295 2.64 2.382 3.983 3.292l2.319 1.57a.75.75 0 0 0 .84 0l2.319-1.57c1.344-.91 2.83-1.997 3.982-3.292 1.157-1.299 2.029-2.867 2.029-4.73 0-2.7-2.37-4.78-5.15-4.78-1.434 0-2.695.672-3.6 1.542-.905-.87-2.167-1.542-3.6-1.542Z'/%3E%3C/svg%3E`;
};

const buildShape = (
  key: ShapeType,
  { fill = '#ffb800', bg = '#b3bcc5' }: { fill?: string; bg?: string },
): { normal: string; fill: string } => {
  switch (key) {
    case ShapeType.star_1:
      return {
        normal: STAR_1(bg),
        fill: STAR_1(fill),
      };
    case ShapeType.star_2:
      return {
        normal: STAR_2(bg),
        fill: STAR_2(fill),
      };
    case ShapeType.heart_1:
      return {
        normal: HEART_1(bg),
        fill: HEART_1(fill),
      };
    default:
      return {
        normal: STAR_1(bg),
        fill: STAR_1(fill),
      };
  }
};

export const buildStyle = ({
  shape,
  size = 36,
  padding = 0,
  margin = 0,
}: {
  shape: ShapeType;
  size?: number;
  padding?: number;
  margin?: number;
}): string => {
  const result: string[] = [];
  result.push(
    `--onirvapp-shape-normal: url("${buildShape(shape, {}).normal}");`,
  );
  result.push(`--onirvapp-shape-fill: url("${buildShape(shape, {}).fill}");`);
  result.push(`--onirvapp-loop-height: ${size}px;`);
  result.push(`--onirvapp-loop-star-size: ${size}px;`);
  result.push(`--onirvapp-loop-star-padding: ${padding}px;`);
  result.push(`--onirvapp-loop-star-margin: ${margin}px;`);
  result.push(`--minus-one: -1;`);
  return result.join('');
};

export const Shape = ({ width }: { width: number }): JSX.Element => {
  return (
    <div className="onirvapp-loop">
      <div className="onirvapp-loop--shape">
        <div className="onirvapp-loop--shape-bg"></div>
        <div
          className="onirvapp-loop--shape-ui"
          style={{ width: `${width}%` }}
        ></div>
      </div>
    </div>
  );
};

const ShapeSelect = ({
  value,
  action,
}: {
  value: number;
  action(value: number): void;
}): JSX.Element => {
  const id = cuid();
  const handleClick = (event: any) => {
    action(_.toNumber(event.target.value));
  };
  return (
    <>
      {_.isNumber(value) ? (
        <div className="flex space-x-1">
          {Array.from([1, 2, 3, 4, 5]).map(v => {
            return (
              <div className="onirvapp--shape-select" key={v}>
                <input
                  id={`${id}-${v}`}
                  type="radio"
                  className="hidden"
                  value={v}
                  onClick={handleClick}
                />
                <label
                  htmlFor={`${id}-${v}`}
                  className={v <= value ? 'active' : ''}
                ></label>
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
};

export default memo(ShapeSelect);
